// src/pages/UserDeepDivePage/Log.tsx
import { useState, SyntheticEvent, useMemo } from 'react'
import { Box, Tabs, Tab, Stack, Typography } from '@mui/material'
import {
    DataGridPro,
    GridColDef,
    GridToolbarContainer,
    GridToolbarExport,
} from '@mui/x-data-grid-pro'
import dayjs from 'dayjs'
import type useUserPage from './useUserPage'
import SectionContainer from '@components/SectionContainer'

type UseUsersPage = ReturnType<typeof useUserPage>
type UserType = NonNullable<UseUsersPage['user']>

interface LogProps {
    accountProfile: UserType['accountProfile']
    adminProfile?: UserType['adminProfile']
}

const FORMAT = 'YYYY/MM/DD HH:mm'

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarExport csvOptions={{ fileName: 'account-log' }} />
        </GridToolbarContainer>
    )
}

export default function Log({ accountProfile }: LogProps) {
    const [tab, setTab] = useState(0)

    const handleTabChange = (_: SyntheticEvent, v: number) => {
        setTab(v)
    }

    const accountEvents = useMemo(() => {
        const ev = [
            { event: 'Account Created', date: accountProfile.created_at },
            { event: 'Account Updated', date: accountProfile.updated_at },
            { event: 'First Logged In', date: accountProfile.first_logged_in! },
            { event: 'Last Logged In', date: accountProfile.last_logged_in! },
        ]
        if (accountProfile.deleted_at) {
            ev.push({
                event: 'Account Deleted',
                date: accountProfile.deleted_at,
            })
        }
        return ev
    }, [accountProfile])

    const rows = useMemo(
        () =>
            accountEvents.map((e, i) => ({
                id: String(i),
                event: e.event,
                date: e.date,
            })),
        [accountEvents]
    )

    const columns: GridColDef[] = [
        { field: 'event', headerName: 'EVENT', flex: 1, sortable: false },
        {
            field: 'date',
            headerName: 'DATE',
            flex: 1,
            sortable: false,
            // valueGetter: ({ row }) => row.date,
            valueFormatter: (value) =>
                value ? dayjs(value as string).format(FORMAT) : '',
        },
    ]

    return (
        <SectionContainer title="Log">
            <Box sx={{ mb: 2 }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                >
                    <Tab label="Habitat Accounts" />
                    <Tab label="Messenger Pigeon" />
                    <Tab label="Habitat Admin" />
                </Tabs>
            </Box>

            {tab === 0 && (
                <Box sx={{ height: 360, width: '100%' }}>
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        hideFooterRowCount
                        slots={{ toolbar: CustomToolbar }}
                    />
                </Box>
            )}

            {tab === 1 && (
                <Typography variant="body2">
                    No Messenger Pigeon data
                </Typography>
            )}

            {tab === 2 && (
                <>
                    <Stack spacing={2}>
                        <Typography variant="body2">No Admin data</Typography>
                    </Stack>
                    <Typography variant="body2">No Admin data</Typography>
                </>
            )}
        </SectionContainer>
    )
}
